import React, { FC, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Modal from 'components/Modal/Modal';
import { ModalWidthEnum } from 'components/Modal/Modal.types';
import { Spinner } from 'components/Spinner/Spinner';
import { Text } from 'components/Typography';
import { Colors } from 'core/CssVariables';
import {
  useGetReleasesQuery,
  useLazyGetReleaseNotesQuery,
  Release,
} from 'redux/apiSlices/releases.slice';
import {
  SReleaseNotesContent,
  SReleaseNotesWrapper,
  SReleaseList,
  SReleaseItem,
  SLoadingContainer,
} from './ReleaseNotesModal.style';

interface ReleaseNotesModalProps {
  visible: boolean;
  onClose: () => void;
  version: string;
}

const ReleaseNotesModal: FC<ReleaseNotesModalProps> = ({
  visible,
  onClose,
  version,
}) => {
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);

  const { data: releasesData, isLoading: isReleasesLoading } =
    useGetReleasesQuery();
  const [
    fetchReleaseNotes,
    { data: markdown, isFetching: isNotesFetching, error: fetchError },
  ] = useLazyGetReleaseNotesQuery();

  // Set initial selected release when modal opens and fetch notes
  useEffect(() => {
    if (visible && releasesData?.releases) {
      const currentRelease = releasesData.releases.find(
        r => r.version === version,
      );
      const releaseToSelect = currentRelease || releasesData.releases[0];
      // Only update if the selected version is different to prevent redundant fetches
      if (selectedRelease?.version !== releaseToSelect?.version) {
        setSelectedRelease(releaseToSelect);
        if (releaseToSelect?.version) {
          fetchReleaseNotes(releaseToSelect.version);
        }
      }
    }
  }, [visible, releasesData, version]);

  const isLoading = isReleasesLoading || isNotesFetching;

  const handleClose = () => {
    setSelectedRelease(null);
    onClose();
  };

  const handleReleaseSelect = (release: Release) => {
    setSelectedRelease(release);
    fetchReleaseNotes(release.version);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <SLoadingContainer>
          <Spinner />
        </SLoadingContainer>
      );
    }

    if (fetchError) {
      return (
        <Text fontlevel={5} color={Colors.ErrorColor}>
          Failed to load release notes
        </Text>
      );
    }

    // Note: react-markdown v8+ is XSS-safe by default as it doesn't render raw HTML.
    // Do NOT add rehype-raw plugin without proper sanitization.
    return <ReactMarkdown>{markdown || ''}</ReactMarkdown>;
  };

  return (
    <Modal
      visible={visible}
      title="Release Notes"
      onCancel={handleClose}
      onOk={handleClose}
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
      size={ModalWidthEnum.ExtraLarge}
      destroyOnClose
    >
      <SReleaseNotesWrapper>
        <SReleaseList>
          <div className="release-list-title">All Releases</div>
          {releasesData?.releases.map(release => (
            <SReleaseItem
              key={release.version}
              $isActive={selectedRelease?.version === release.version}
              onClick={() => handleReleaseSelect(release)}
            >
              <div className="version">
                v{release.version}
                {release.version === version && (
                  <span className="current-badge">Current</span>
                )}
              </div>
              <div className="date">{release.date}</div>
            </SReleaseItem>
          ))}
        </SReleaseList>
        <SReleaseNotesContent>{renderContent()}</SReleaseNotesContent>
      </SReleaseNotesWrapper>
    </Modal>
  );
};

export default ReleaseNotesModal;
